import * as _ from "lodash";
import * as angular from "angular";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { EffortDataProvider } from "../../data-provider/effort.dp";
import { GroupDataProvider } from "../../data-provider/group.dp";
import { Effort } from "../../models/effort.model";
import { Group } from "../../models/group.model";
import { Integration } from "../../models/integration.model";
import { PermissionCheck } from "../../shared/permission";
import { DialogUtils } from "../../helper/dialog.helper";
import { IntegrationData } from "../../utils/interfaces";
import { ScreenUtils } from "../../helper/screen.helper";
import { ToastUtils } from "../../helper/toast.helper";
import { IntegrationDataProvider } from "../../data-provider/integration.dp";
import { Utils } from "../../helper/utils.helper";
import { NavigationBarHelperService } from "../navigation/navigation-bar.helper";

export class IntegrationController {
    public installed = false;
    public loading = true;
    public active_tab = "main";
    public integrationSupportLink: string | null = null;
    public effortIntegrations = ["callhub"];
    public data: IntegrationData = {
        consent_volunteer: false,
        consent_newsletter: false,
        consent_updates: false,
        group: null,
        audience: null,
    };
    public groups: Group[] = [];
    public lists: any[] = [];
    public audiences: any = null;
    public product_name: string;
    public integrationKey: string | undefined;
    public needsMapping: boolean;
    public efforts: Effort[] = [];
    public loadingMessage = "Checking for integration...";
    public showFilter = false;
    public integration: Integration;
    public saved = false;
    public headline: string | undefined;
    public subheading: string | undefined;

    private readonly $scope: any;
    private readonly $window: any;
    public readonly __env: any;

    private readonly dialog: DialogUtils;
    private readonly toast: ToastUtils;
    public readonly screen: ScreenUtils;
    public dataProvider: IntegrationDataProvider;

    constructor(
        $scope: any,
        $window: any,
        $routeParams: any,
        __env: any,
        DialogUtils: DialogUtils,
        ToastUtils: ToastUtils,
        Utils: Utils,
        ScreenUtils: ScreenUtils,
        EffortDataProvider: EffortDataProvider,
        IntegrationDataProvider: IntegrationDataProvider,
        CampaignDataProvider: CampaignDataProvider,
        GroupDataProvider: GroupDataProvider,
        PermissionCheck: PermissionCheck,
        NavigationBarHelperService: NavigationBarHelperService
    ) {
        Utils.accessCheck();
        Utils.runWootric();

        this.$scope = $scope;
        this.$window = $window;
        this.__env = __env;

        this.dialog = DialogUtils;
        this.toast = ToastUtils;
        this.screen = ScreenUtils;
        this.dataProvider = IntegrationDataProvider;

        this.product_name = __env.product_name;
        this.integrationKey = $routeParams.integrationKey;

        if (this.integrationKey == "callhub") {
            this.data.send_updates = true;
            this.data.pull_updates = true;
        }

        if (!this.integrationKey) {
            this.integrationKey = "salesforce"; //hack for now, figure it out once needed. Route param is not set as this does not match with the route with the variable in it
            this.data = {
                confirmation_email: false,
                send_new_voters: false,
                send_voter_updates: false,
            };
        }

        if (this.effortIntegrations.indexOf(this.integrationKey) > -1) {
            this.data.effort = null;
        }

        this.needsMapping =
            ["salesforce", "callhub"].indexOf(this.integrationKey) > -1;

        EffortDataProvider.getActiveEfforts().then(
            (efforts: Effort[]) => (this.efforts = efforts)
        );

        PermissionCheck.getConsentSettings().then((consentSettings: any) => {
            this.showFilter =
                consentSettings["SIGNATURE"].attributes.owner === 1 ||
                CampaignDataProvider.activeCampaign.campaignregion == "eu";
        });

        if (this.integrationKey == "salesforce") {
            this.dataProvider
                .getInProgressIntegration()
                .then((integration: Integration) => {
                    this.integration = integration;
                    this.integrationDetails(integration);
                });
        } else {
            this.dataProvider.getIntegration(this.integrationKey).then(
                (integration: Integration) => {
                    this.integration = integration;
                    this.integration.status =
                        this.integration.status || "Inactive";
                    if (integration.available) {
                        this.integrationDetails(integration);
                    } else {
                        this.notAvailable(integration);
                        this.$window.ga("send", {
                            hitType: "event",
                            eventCategory: "integrationStore",
                            eventAction: integration.title + " clicked",
                            eventLabel: integration.title + " clicked",
                            eventValue: 1,
                        });
                    }
                },
                () => this.notAvailable(this.integration)
            );
        }

        GroupDataProvider.getAllGroups().then(
            (groups: Group[]) => (this.groups = groups)
        );

        NavigationBarHelperService.setShowBackButton(true);
    }

    $onInit() {
        this.$scope.$watch(
            () => this.data,
            () => (this.saved = false),
            true
        );
    }

    saveDisabled(data: any): boolean {
        return (
            !data.audience &&
            this.dataProvider.noAudience.indexOf(this.integrationKey) == -1
        );
    }

    integrationDetails(integration: Integration): void {
        const supportLink = this.__env[integration.key + "_how_to"];
        if (supportLink) {
            this.integrationSupportLink = supportLink;
        }
        if (integration.key === "l2") {
            this.notInstalled(integration);
            return;
        }

        this.dataProvider
            .getIntegrationDetails(integration)
            .then((res) => {
                this.loading = false;
                if (this.integrationKey == "callhub") {
                    this.installed = res.installed
                        ? res.installed
                        : res.data.installed && res.data.authenticated;
                    this.data = angular.extend(this.data, res.data.settings);
                } else if (this.integrationKey == "salesforce") {
                    this.installed = res.installed
                        ? res.installed
                        : res.data.installed;
                    if (!this.integration.settings.listview_id) {
                        this.integration.settings.listview_id = null;
                    }
                    this.data = angular.extend(this.data, res.data.settings);

                    if (this.lists.length == 0) {
                        this.getListViews();
                    }
                } else {
                    this.installed = res.success;
                }
                if (this.installed) {
                    let audience = null;
                    if (res.data.length > 1) {
                        this.audiences = res.data;
                        audience = this.audiences.filter((item: any) => {
                            return item.selected;
                        })[0];
                    } else {
                        audience = res.data[0];
                    }
                    if (audience) {
                        this.dataProvider
                            .setIntegrationDetails(integration.key, audience.id)
                            .then((res: any) => {
                                this.data = _.merge(this.data, res.settings);
                            });
                    }
                } else {
                    this.notInstalled(integration);
                }
            })
            .catch(() => this.notInstalled(integration));
    }

    notAvailable(integration: Integration): void {
        this.loading = false;
        this.headline =
            "Integration for " +
            integration.title +
            " currently not available!";
        this.subheading = "Coming soon";
        if (integration.key == "l2") {
            this.subheading =
                "Click here to purchase and get assistance importing!";
        }
    }

    notInstalled(integration: Integration): void {
        this.loading = false;
        this.headline =
            "Not authenticated with " + integration.title + " just yet!";
        this.subheading =
            "Connect your " +
            integration.title +
            " account with " +
            this.product_name +
            " in order to sync your data";

        if (integration.key == "l2") {
            this.headline =
                "Canvass strategically in Ecanvasser with L2’s trusted data at your fingertips.";
            this.subheading =
                "Login to your L2 Data account to import your data to Ecanvasser.";
        }
    }

    installIntegration(key: string): void {
        if (key === "l2") {
            const l2URL = "https://www.l2datamapping.com";
            this.$window.open(l2URL, "_blank");
        } else {
            this.loadingMessage =
                "Installing " + this.integration.title + "...";
            this.loading = true;
            this.dataProvider.install(key).then((url: string) => {
                this.$window.location = url;
            });
        }
    }

    uninstallIntegration(key: string, showDeleteSettings: boolean): void {
        this.dialog
            .show({
                component: "vc-integration-uninstall-dialog",
                scope: this.$scope,
                locals: {
                    integration: this.integration,
                    product_name: this.product_name,
                    showDeleteSettings: showDeleteSettings,
                },
                ariaLabel: "Uninstall Integration",
            })
            .then((deleteSettings: any) => {
                this.loadingMessage =
                    "Uninstalling " + this.integration.title + "...";
                this.loading = true;
                this.dataProvider.uninstall(key, deleteSettings).then(() => {
                    this.loading = false;
                    this.installed = false;
                    this.integration.status = "Inactive";
                    this.notInstalled(this.integration);
                });
            });
    }

    save(key: string, reloadLists: boolean): void {
        if (this.integration) {
            this.loadingMessage =
                "Updating " + this.integration.title + " preferences...";
            this.loading = true;

            this.dataProvider.updateSettings(key, this.data).then(() => {
                this.loading = false;
                this.saved = true;
                if (reloadLists) {
                    this.getListViews(false);
                }
            });
        }
    }

    updateListView(listViewId: string): void {
        this.loadingMessage = "Setting List View...";
        this.loading = true;

        this.dataProvider
            .updateSettings(
                this.integrationKey,
                _.merge(this.data, { listview_id: listViewId })
            )
            .then(() => {
                this.integration.settings.listview_id = listViewId;
                this.loading = false;
            });
    }

    sync(): void {
        this.integration.reset();
        this.integration.status = "Working";
        this.dataProvider
            .pull(this.integration.key)
            .then((integration: Integration) => {
                this.integration.status = integration.status;
            })
            .catch(this.handleFailure.bind(this));
    }

    openFieldMapping(integration: Integration, ev: any): void {
        this.dialog
            .show({
                component: "vc-integration-field-mapping",
                targetEvent: ev,
                clickOutsideToClose: false,
                skipHide: true,
                ariaLabel: "Field Mapping",
                size: "x-large",
                locals: {
                    selectedIntegration: integration,
                },
            })
            .then((integration: Integration) => {
                _.merge(this.integration, integration);
            });
    }

    openIntercomChat(): void {
        this.$window.Intercom("showNewMessage");
    }

    openErrorPage(integration: Integration, ev: any): void {
        this.$window.location.href =
            "./#/integration/" + integration.key + "/errors";
    }

    openStatusMapping(integration: Integration, ev: any): void {
        this.dialog
            .show({
                component: "vc-integration-status-mapping",
                targetEvent: ev,
                clickOutsideToClose: false,
                skipHide: true,
                ariaLabel: "NationBuilder Mapping",
                locals: {
                    selectedIntegration: integration,
                },
            })
            .then((integration: Integration) => {
                _.merge(this.integration, integration);
            });
    }

    handleFailure(): void {
        this.integration.status = "Active";
        this.toast.error(
            "An error has occurred, contact " + this.__env.support_email,
            10000
        );
    }

    isImportRunning(): boolean {
        return (
            !!this.integration &&
            this.integration.status != "Active" &&
            this.integration.status != "Inactive" &&
            this.integration.status != "Error" &&
            this.integration.status != "RateLimited" &&
            this.integration.status != "TimeOut" &&
            this.integration.status != "Misconfigured" &&
            this.integration.status != "LimitExceeded"
        );
    }

    isInserting(): boolean {
        return !!this.integration && this.integration.status == "Inserting";
    }

    getListViews(force: boolean = false): void {
        if (
            !force &&
            ["Unauthorized", "Restricted", "RateLimited"].indexOf(
                this.integration.status
            ) > -1
        ) {
            return;
        }

        if (force) {
            this.integration.status = "Active";
        }

        this.dataProvider.getListViews(this.integrationKey).then((res: any) => {
            this.lists = res.listviews;
            this.integration.status = res.integration.status;

            const findList = this.lists.filter((list: any) => {
                return list.id == this.integration.settings.listview_id;
            });

            if (!findList.length) {
                this.integration.settings.listview_id = null;
            }

            if (!this.lists.length) {
                this.listViewError();
            }
        });
    }

    listViewError(): void {
        let alert = this.dialog
            .alert()
            .title(
                "There was an issue retrieving your  " +
                    this.integration.title +
                    " list(s)"
            )
            .htmlContent(
                "<p>Please try refreshing the page. Should the problem persist, try re-installing the app</p>"
            )
            .ok("Dismiss");
        this.dialog.showPreConfiguredDialog(alert);
    }

    notAllowed(): boolean {
        return (
            !!this.integration &&
            (this.integration.status == "Unauthorized" ||
                this.integration.status == "Restricted" ||
                this.integration.status == "RateLimited")
        );
    }
}

angular
    .module("integration")
    .controller("IntegrationController", IntegrationController);
